import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import PipelineFunnelChart from "@/components/dashboard/PipelineFunnelChart";
import RevenueTrendChart from "@/components/dashboard/RevenueTrendChart";
import {
  Users,
  Lightning,
  CheckCircle,
  CurrencyInr,
  ArrowUpRight,
  ClockCounterClockwise,
  Receipt,
} from "@phosphor-icons/react";

function MetricCard({ label, value, sub, icon: Icon, accent, testId, onClick }) {
  const interactive = typeof onClick === "function";
  return (
    <div
      className={
        "bg-white border border-slate-200 rounded-lg p-6 card-hover " +
        (interactive ? "cursor-pointer hover:border-slate-300" : "")
      }
      data-testid={testId}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            {label}
          </div>
          <div className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900">
            {value}
          </div>
          {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
        </div>
        <div
          className={`w-10 h-10 rounded-md flex items-center justify-center ${accent}`}
        >
          <Icon size={20} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="p-8 text-slate-500" data-testid="dashboard-loading">
        Loading dashboard…
      </div>
    );
  }

  const d = data || {};
  const conversionRate =
    d.total_leads > 0 ? Math.round((d.conversions / d.total_leads) * 100) : 0;

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      {/* Sticky header */}
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Overview
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Dashboard
            </h1>
          </div>
          <button
            onClick={() => navigate("/sales/customers")}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            data-testid="dashboard-cta-customers"
          >
            View all customers <ArrowUpRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div className="p-8 space-y-8">
        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 fade-up">
          <MetricCard
            label="Total Leads"
            value={d.total_leads || 0}
            sub="All-time pipeline"
            icon={Users}
            accent="bg-blue-50 text-blue-700"
            testId="metric-total-leads"
          />
          <MetricCard
            label="Active"
            value={d.active_customers || 0}
            sub="Engaged + Negotiation"
            icon={Lightning}
            accent="bg-indigo-50 text-indigo-700"
            testId="metric-active-customers"
          />
          <MetricCard
            label="Conversions"
            value={d.conversions || 0}
            sub={`${conversionRate}% conversion rate`}
            icon={CheckCircle}
            accent="bg-emerald-50 text-emerald-700"
            testId="metric-conversions"
          />
          <MetricCard
            label="Revenue"
            value={`₹${Number(d.revenue_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
            sub={`${d.orders_count || 0} orders`}
            icon={CurrencyInr}
            accent="bg-amber-50 text-amber-700"
            testId="metric-revenue"
          />
          <MetricCard
            label="Outstanding"
            value={`₹${Number(d.outstanding_receivables?.total || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
            sub={(() => {
              const r = d.outstanding_receivables || {};
              if (!r.count) return "All invoices paid";
              const oldest = r.oldest_age_days
                ? ` · oldest ${r.oldest_age_days}d`
                : "";
              return `${r.count} unpaid invoice${r.count === 1 ? "" : "s"}${oldest}`;
            })()}
            icon={Receipt}
            accent="bg-rose-50 text-rose-700"
            testId="metric-receivables"
            onClick={() => navigate("/invoices/follow-ups")}
          />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <RevenueTrendChart trend={d.revenue_trend} />
          </div>
          <div className="lg:col-span-2">
            <PipelineFunnelChart statusBreakdown={d.status_breakdown} />
          </div>
        </div>

        {/* Lower grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Recent customers */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  Recent customers
                </h3>
                <div className="text-xs text-slate-500 mt-0.5">
                  Latest updates from your pipeline
                </div>
              </div>
              <button
                onClick={() => navigate("/sales/customers")}
                className="text-sm text-blue-600 font-medium hover:text-blue-700"
                data-testid="recent-customers-view-all"
              >
                View all →
              </button>
            </div>
            <div className="divide-y divide-slate-100">
              {(d.recent_customers || []).length === 0 && (
                <div className="px-6 py-10 text-center text-sm text-slate-500">
                  No customers yet.
                </div>
              )}
              {(d.recent_customers || []).map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/sales/customers/${c.id}`)}
                  className="w-full text-left px-6 py-4 hover:bg-slate-50 transition-colors flex items-center justify-between gap-4"
                  data-testid={`recent-customer-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.company || "—"}
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </button>
              ))}
            </div>
          </div>

          {/* Recent activity */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="duotone" className="text-slate-500" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">
                Recent activity
              </h3>
            </div>
            <div className="px-6 py-5">
              {(d.recent_activities || []).length === 0 && (
                <div className="text-sm text-slate-500 py-6 text-center">
                  Activity will appear here.
                </div>
              )}
              <div className="timeline-line space-y-5">
                {(d.recent_activities || []).map((a) => (
                  <div key={a.id} className="relative" data-testid={`activity-${a.id}`}>
                    <span className="timeline-dot" />
                    <div className="text-sm text-slate-800">{a.details || a.action}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {new Date(a.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
